import { useEffect, useId, useState, type ReactNode } from "react";

/**
 * The controls the inspector panels are built from. They live apart from the
 * panels so a new panel — text, composition — can use them without reaching
 * back into App.tsx for them.
 */

export function IconButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className="flex size-5 shrink-0 items-center justify-center rounded-[5px] border border-rule text-soft transition-colors hover:border-pine hover:text-pine disabled:opacity-35"
    >
      {children}
    </button>
  );
}


/**
 * One labelled cluster of controls inside the layout panel.
 *
 * `first` exists so the divider belongs to the group below it rather than
 * being a separate element between them: a list of groups then needs no
 * knowledge of where the separators go.
 */
export function Group({
  label,
  first = false,
  children,
}: {
  label: string;
  first?: boolean;
  children: ReactNode;
}) {
  return (
    <section
      className={`flex flex-col gap-2 ${first ? "" : "border-t border-rule/70 pt-3.5"}`}
    >
      <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted">{label}</span>
      {children}
    </section>
  );
}


/**
 * A measurement as both a slider and a pixel count.
 *
 * The slider is for arranging by eye; the number is for the times a clip has to
 * be exactly 480 wide or exactly 40 from the left. Typing is only committed on
 * blur or Enter, so a half-typed "4" of "480" does not briefly resize the clip
 * to four pixels.
 *
 * Note the shape: a <label> pointing at the slider by id, rather than one
 * wrapping the whole row. Wrapping put the number field inside the slider's own
 * label, which makes clicking the number activate the slider — the sort of
 * thing that works by accident with a mouse and not at all with a keyboard.
 */
/**
 * A row of mutually exclusive choices, all visible.
 *
 * Used for both the aspect lock and the fit mode, which had the same problem in
 * two forms: a single button labelled with the current value, which cannot say
 * whether it is reporting a state or offering to change it.
 */
export function Segmented<T>({
  label,
  options,
  selected,
  onSelect,
}: {
  label: string;
  options: { value: T; label: string; hint: string }[];
  selected: T;
  onSelect: (value: T) => void;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className="flex overflow-hidden rounded-[8px] border border-rule"
    >
      {options.map((option, index) => (
        <button
          key={option.label}
          type="button"
          title={option.hint}
          aria-pressed={selected === option.value}
          onClick={() => onSelect(option.value)}
          className={`flex-1 py-1.5 text-[11px] font-medium transition-colors ${
            index > 0 ? "border-l border-rule" : ""
          } ${
            selected === option.value
              ? "bg-pine text-cream"
              : "bg-cream-2/40 text-pine hover:bg-cream-2"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function MeasureField({
  label,
  fraction,
  pixels,
  min,
  max,
  onChange,
}: {
  label: string;
  fraction: number;
  /** The composition's extent on this axis, which the fraction is of. */
  pixels: number;
  min: number;
  max: number;
  onChange: (fraction: number) => void;
}) {
  const sliderId = useId();
  const exact = Math.round(fraction * pixels);
  const [text, setText] = useState(String(exact));
  const [editing, setEditing] = useState(false);
  // While not being typed in, the field follows the slider.
  useEffect(() => {
    if (!editing) setText(String(exact));
  }, [exact, editing]);

  const commit = () => {
    setEditing(false);
    const value = Number(text);
    if (Number.isFinite(value)) onChange(value / pixels);
    else setText(String(exact));
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between text-[12px] text-soft">
        <label htmlFor={sliderId}>{label}</label>
        <span className="font-mono text-[11px] text-muted">{Math.round(fraction * 100)}%</span>
      </div>
      <div className="flex items-center gap-2">
        <input
          id={sliderId}
          type="range"
          className="fader min-w-0 flex-1"
          min={min}
          max={max}
          step={0.005}
          value={fraction}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        <span className="flex shrink-0 items-center gap-1 rounded-[6px] border border-rule bg-[#fbfaf5] px-1.5">
          <input
            type="number"
            value={text}
            aria-label={`${label} in pixels`}
            className="w-12 bg-transparent py-1 text-right font-mono text-[11px] text-ink outline-none"
            onFocus={() => setEditing(true)}
            onChange={(event) => setText(event.target.value)}
            onBlur={commit}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                commit();
                event.currentTarget.blur();
              }
            }}
          />
          <span className="font-mono text-[9px] text-muted">px</span>
        </span>
      </div>
    </div>
  );
}

export function Slider({
  label,
  value,
  min,
  max,
  step,
  format,
  onChange,
  action,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  format: (value: number) => string;
  onChange: (value: number) => void;
  /** An optional shortcut beside the readout, such as a fixed step. */
  action?: ReactNode;
}) {
  const sliderId = useId();
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2 text-[12px] text-soft">
        <label htmlFor={sliderId}>{label}</label>
        <span className="flex items-baseline gap-2">
          {action}
          <span className="font-mono text-[11px] text-muted">{format(value)}</span>
        </span>
      </div>
      <input
        id={sliderId}
        type="range"
        className="fader"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </div>
  );
}

